import {
  IconDefinition,
  faBuilding,
  faCreditCard,
  faHandHoldingDollar,
  faInboxIn,
  faInboxOut,
  faMoneyBill,
  faReceipt,
  faRobot,
  faUser,
  faUserGroup,
  faWallet,
} from '@fortawesome/pro-regular-svg-icons';
import {
  CreditTransactionType,
  Payment,
  PayoutType,
  UserType,
} from '@enrollhere/classes';

/**
 * Minimal shape shared by CreditAccount, CreditTransaction.account,
 * AccountTransfer.fromAccount/toAccount and the user-balances response rows.
 */
export interface WalletLike {
  owner?: { id?: string; type?: UserType } | undefined;
  permanentTags?: { list?: string[] } | undefined;
}

export enum WalletKind {
  PERSONAL = 'PERSONAL',
  TEAM = 'TEAM',
  AGENCY = 'AGENCY',
}

export const walletKindMap: {
  [key in WalletKind]: {
    title: string;
    description: string;
    icon: IconDefinition;
    badgeClass: string;
  };
} = {
  [WalletKind.PERSONAL]: {
    title: 'Personal',
    description: 'Wallet owned by the agent',
    icon: faUser,
    badgeClass: 'badge-info',
  },
  [WalletKind.TEAM]: {
    title: 'Team',
    description: 'Agency-owned wallet assigned to a team',
    icon: faUserGroup,
    badgeClass: 'badge-secondary',
  },
  [WalletKind.AGENCY]: {
    title: 'Agency',
    description: 'Agency-owned wallet',
    icon: faBuilding,
    badgeClass: 'badge-neutral',
  },
};

/**
 * Classify a wallet from data present on the account itself.
 *
 * - Owned by an AGENT → Personal
 * - Otherwise, carries permanent (team) tags → Team
 * - Otherwise, owned by an agency-side user → Agency
 *
 * Mirrors the existing "Team Wallet" rule (any permanent tag on an
 * agency-owned account), extended with the other two kinds.
 */
export function getWalletKind(
  account?: WalletLike | null
): WalletKind | undefined {
  if (!account?.owner?.type) return undefined;

  if (account.owner.type === UserType.AGENT) return WalletKind.PERSONAL;

  if (account.permanentTags?.list?.length) return WalletKind.TEAM;

  return WalletKind.AGENCY;
}

export enum TransactionDirection {
  ADDED = 'ADDED',
  SPENT = 'SPENT',
}

export const transactionDirectionMap: {
  [key in TransactionDirection]: { title: string; textClass: string };
} = {
  [TransactionDirection.ADDED]: { title: 'Added', textClass: 'text-success' },
  [TransactionDirection.SPENT]: { title: 'Spent', textClass: 'text-error' },
};

/** Sign of the ledger amount decides direction; type does not. */
export function getTransactionDirection(
  payment?: Pick<Payment, 'transaction'> | null
): TransactionDirection | undefined {
  const amount = payment?.transaction?.amount;
  if (amount === undefined || amount === null || amount === 0) {
    return undefined;
  }
  return amount > 0 ? TransactionDirection.ADDED : TransactionDirection.SPENT;
}

export interface FundingSource {
  title: string;
  icon: IconDefinition;
  /** The other wallet involved (transfers / refunds), when known */
  counterpart?: WalletLike;
}

const ownFundsTitle: { [key in WalletKind]: string } = {
  [WalletKind.PERSONAL]: 'Own funds',
  [WalletKind.TEAM]: 'Team funds',
  [WalletKind.AGENCY]: 'Agency funds',
};

/**
 * Describe where the money for a ledger row came from (additions) or which
 * pool it was taken from (subtractions). Derived only from fields the
 * payment writers already populate.
 */
export function getFundingSource(
  payment?: Pick<
    Payment,
    'payoutType' | 'transaction' | 'allowance' | 'accountTransfer'
  > | null
): FundingSource | undefined {
  if (!payment) return undefined;

  const transactionType = payment.transaction?.type;
  const amount = payment.transaction?.amount ?? 0;

  if (transactionType === CreditTransactionType.CTT_TRANSFER_IN) {
    return {
      title: 'Transfer in',
      icon: faInboxIn,
      counterpart: payment.accountTransfer?.fromAccount,
    };
  }

  if (transactionType === CreditTransactionType.CTT_TRANSFER_OUT) {
    return {
      title: 'Transfer out',
      icon: faInboxOut,
      counterpart: payment.accountTransfer?.toAccount,
    };
  }

  if (transactionType === CreditTransactionType.CTT_REFUND) {
    return amount >= 0
      ? {
          title: 'Refund received',
          icon: faMoneyBill,
          counterpart: payment.accountTransfer?.fromAccount,
        }
      : {
          title: 'Refund issued',
          icon: faMoneyBill,
          counterpart: payment.accountTransfer?.toAccount,
        };
  }

  if (transactionType === CreditTransactionType.CTT_FEE) {
    return { title: 'Processing fee', icon: faReceipt };
  }

  if (
    payment.payoutType === PayoutType.PT_TOP_UP ||
    transactionType === CreditTransactionType.CTT_TOP_UP
  ) {
    return { title: 'Card payment', icon: faCreditCard };
  }

  if (payment.payoutType === PayoutType.PT_AUTO_TOP_UP) {
    return { title: 'Auto add funds', icon: faRobot };
  }

  // Only usage rows are "taken from" a wallet pool. Commission payouts
  // (PT_SEND_PAYOUT, PT_AUTO_CALCULATED, …) share this table but are
  // income, not wallet spend, so they get no funding line.
  const isUsage =
    payment.payoutType === PayoutType.PT_USAGE ||
    transactionType === CreditTransactionType.CTT_USAGE;
  if (!isUsage) return undefined;

  if (payment.allowance) {
    return { title: 'Team allowance', icon: faHandHoldingDollar };
  }

  const kind = getWalletKind(payment.transaction?.account);
  if (kind) {
    return { title: ownFundsTitle[kind], icon: faWallet };
  }

  return undefined;
}
